<!-- ONE LINE. The modal is fixed at min(84rem, 95vh) and the step body is the only region that
     scrolls, so every row added here is a row taken from the advisor's working area. The bar
     therefore sits BETWEEN the two figures rather than under them, and carries its own labels
     inside its segments -- which is also what fixes the problem the full-width version had: the
     legend sat in the right third of the header while the runs it named spanned the whole width,
     ~900px away, so nothing connected a figure to its segment. A label written on its own run
     cannot be misattributed.

     ⚠️ TWO figures on the line, and everything else is on the bar. Custom Holdings and
     Custodial Cash both used to stand here as figures of their own: Custodial Cash went when
     cash became opt-in on step 1 (it is not in the opening balance any more, so a term stating
     it explained nothing), and Custom Holdings became a RUN rather than a figure -- it is the
     biggest part of most accounts, so as a number in the row it dwarfed the one beside it, and
     as a run it is simply the width of what is staying put. -->
<div class="flex items-center px-20 py-8 overflow-x-auto border-b border-gray-lighter gap-x-20 whitespace-nowrap">
  <!-- THE ACCOUNT AS IT STANDS -- context, and the bar's 100%, not a peer of the runs beside
       it. They partition it; it is the thing being partitioned.

       ⚠️ Which is also the argument for a bar over printing the equation with `+` and `=`, as
       this row once did. Orion reports the account total and the sleeve holdings on two bases
       that can disagree by a cent (see `investable` in the store), and a cent is invisible in a
       bar and a lie in an equation.

       ⚠️ DATA-DRIVEN rather than one hardcoded figure, because WHAT the request is measured
       against differs by type: the account for a reallocation, the amount the advisor typed for
       a contribution or distribution. See `contextTerms` -- which is why this survived develop's
       rewrite of the row.

       ⚠️ Custodial Cash used to be a second figure here and is GONE (ACMTECH-970/971): releasing
       cash became an opt-in decision on step 1, so the figure that mattered stopped being what
       the sleeve holds and became what this request was given -- which lives inside the runs,
       not beside them.

       ⚠️ THE EMPHASIS IS A FLAG, NOT THE FIRST POSITION, and the two stopped coinciding when the
       account total joined the move-money row (AndyP, 2026-09-22). The total is printed first
       because context reads before subject, but the bar partitions the AMOUNT -- so the amount
       keeps the bold mint. Bolding by position would claim the runs add up to the account. -->
  @for (c of contextTerms(); track c.label) {
    <div class="flex flex-col">
      <span class="text-lg"
        [class.font-bold]="c.emphasis"
        [class.text-mint-darker]="c.emphasis"
        [class.font-light]="!c.emphasis"
        [class.text-gray-darkest]="!c.emphasis">{{ c.value | currency }}</span>
      <span class="mt-1 text-xs tracking-wide uppercase text-gray">{{ c.label }}</span>
    </div>
  }

  <!-- THE ACCOUNT AS ONE BAR, filling everything between the two figures.

       Allocated to models, then Custom Holdings, then the gap -- see `barSegments` for why that
       order is fixed rather than following the wizard's steps.

       ⚠️ `aria-hidden`, deliberately. Every figure it draws is in the DOM as text elsewhere
       (each run's own label, and Left to allocate beside it), so a screen reader gains nothing
       from percentages and loses time to five more nodes. It is a second rendering of
       accessible data, not a source of its own.

       ⚠️ The transition is inline, so it cannot be suppressed under `prefers-reduced-motion` --
       there is no component stylesheet to put a media query in. Judged acceptable: a 250ms width
       tween, no movement across the page, no flashing, no parallax. -->
  <!-- ⚠️ EVERYTHING BELOW IS SUPPRESSED ON THE FUNDING STEP -- the bar and the remainder,
       leaving the account total alone on the row. The amount is being SET there, so a
       "left to allocate" box would read the whole of it back as though it were already
       outstanding, and the bar has no basis to partition until it is entered.

       The strip used to be hidden on funding altogether for exactly that reason. What was
       missing was the one figure that is true before any of it is decided (AndyP,
       2026-09-22). -->
  @if (!isContextOnly()) {
    <!-- THE BAR'S COLUMN, ruled off on both sides.

         ⚠️ `self-stretch` against the row's `items-center`, so the rules run the full height of the
         strip rather than only as tall as the bar. Without it the column is as tall as its content,
         which changes with the legend — the dividers would grow and shrink with the data, and on a
         strip with no legend they would be shorter than the two-line figures beside them.

         `justify-center` then re-centres the bar inside that full height, which is what
         `items-center` was doing for it before.

         ⚠️ The rules earn their place rather than being decoration: TOTAL ACCOUNT VALUE sat directly
         against the legend's first entry, so two unrelated figures read as one phrase. `px-20` is
         what actually separates them; the rule is what says the separation is deliberate. -->
    <div class="flex flex-col justify-center self-stretch flex-1 px-20 border-l border-r border-gray-lighter"
      style="min-width: 24rem">
      <!-- ⚠️ THE TRACK'S TOOLTIP IS THE REMAINDER, not the account total (AndyP, 2026-09-22).
           This sits on the container, so it is what answers when the advisor hovers the GREY —
           and the grey is the empty tail, which is Left to allocate. Naming the account there
           described the one thing the reader was demonstrably not pointing at.

           ⚠️ `remainderCaption()`, the same words the figure beside the bar uses, so the two
           cannot disagree — and it says "Left to place" / "Left to distribute" / "Over-allocated"
           according to the request type and state rather than assuming a reallocation. -->
      <!-- ⚠️ `cursor-help`, not `cursor-pointer`. Everything here is explanatory -- the runs and
           the track answer on hover and nothing is clickable -- and a pointer promises a click
           that does nothing. It is also what every other tooltip in this wizard uses, so the two
           would have disagreed about the same gesture. One word to swap if you want the pointer. -->
      <div class="flex overflow-hidden rounded-full cursor-help bg-gray-lighter"
        style="height: 2.2rem"
        aria-hidden="true"
        [title]="(remainderCaption() | uppercase) + ' — ' + (investableTerm().value | currency)">
        @for (seg of barSegments(); track seg.key) {
          <!-- ⚠️ `min-width` IS THE POINT, and it is a deliberate inaccuracy. A run of 0.11% is one
               pixel on a 980px bar -- present in the DOM, invisible on screen -- so every non-zero
               run gets a floor and is drawn wider than its share. A 0.11% run at 0.6rem overstates
               itself several times over.

               Judged worth it, and the reasoning is worth keeping: nobody measures a stacked bar,
               the exact figures are always on screen beside it, and the alternative is a part of
               the account that silently is not drawn. The borrowed width comes out of the GAP,
               which flexbox hands over for free while there is slack -- and the gap is the least
               precise thing here, being the one region with no figure written on it. Once the bar
               is full the runs shrink proportionally instead, so the biggest lends the most.

               ⚠️ Which is also why the threshold below is not simply "can the text fit": a run
               forced up to its floor still cannot hold text, and stretching to fit the label would
               turn a small inaccuracy into a large one. -->
          <div class="flex items-center justify-center px-5 overflow-hidden"
            [style.color]="seg.ink"
            [style.backgroundColor]="seg.fill"
            [style.width.%]="seg.pct"
            style="transition: width 250ms ease-out; font-size: 1.05rem; min-width: 0.6rem"
            [title]="((seg.tooltipLabel ?? seg.label) | uppercase) + ' — ' + (seg.value | currency)">
            @switch (inside(seg)) {
              @case ('full') {
                <!-- ⚠️ `legendLabel` here as well as in the legend (AndyP, 2026-09-22), so the
                     run says "(NOT INVESTABLE)" when it has the width for it.

                     🛑 `fits` is PURE GEOMETRY -- percentage thresholds, 24% for a label and
                     10% for a figure -- and measures no text. It was tuned against the short
                     captions, so a run between about 24% and the width this longer one needs
                     will clip inside `overflow-hidden` rather than fall back. Watch the Cash
                     On Hold run at middling widths; if it truncates, the honest fixes are a
                     higher threshold for runs carrying a long caption, or leaving the run
                     with the bare label as it was. -->
                <span class="tracking-wide uppercase">{{ seg.legendLabel ?? seg.label }}</span>
                <span class="ml-5 font-semibold">{{ seg.value | currency }}</span>
              }
              @case ('value') {
                <span class="font-semibold">{{ seg.value | currency }}</span>
              }
            }
          </div>
        }

      </div>

      <!-- THE LEGEND -- every run's figure, under the bar, when the bar cannot carry them itself.

           ⚠️ INSIDE the bar's own column, which is the whole reason it can align. `justify-between`
           puts the first entry on the bar's left edge and the last on its right, so each figure sits
           under the end of the run it names. A legend anywhere else in the row would have to match
           the width of the total-value column beside it, which changes with the figure.

           ⚠️ ALL the runs, not only the cramped one. A legend naming just the narrow run, while the
           wide runs kept their own labels, split one total across two places and read worse than
           either -- see `inside`. When this shows, the bar is blank.

           🟢 Only rendered when a run cannot hold its own figure. `crampedSegments()` IS that
           condition, so no flag and no measurement is needed: on an ordinary strip this costs no
           vertical space at all, which matters in a modal where the step body is the only region
           that scrolls. -->
      @if (showLegend()) {
        <div class="flex items-center justify-between mt-5 gap-x-15 text-gray-darkest"
          style="font-size: 1.05rem" data-spill-legend>
          @for (seg of legendSegments(); track seg.key) {
            <!-- ⚠️ THE RUN'S OWN TOOLTIP, CARRIED DOWN HERE (AndyP, 2026-09-22). The bar states it
                 on every segment, and the legend only exists because one run got too cramped to
                 hold its own text — so hovering was answering on a crowded request and going
                 quiet on a very crowded one, which is the wrong way round. Same expression as the
                 run above, so the two cannot drift. -->
            <span class="flex items-center cursor-help gap-x-5 whitespace-nowrap"
              [title]="((seg.tooltipLabel ?? seg.label) | uppercase) + ' — ' + (seg.value | currency)">
              <!-- The swatch is what ties the figure to its run once the bar itself is blank. -->
              <span class="rounded-full flex-shrink-0" [style.backgroundColor]="seg.fill"
                style="width: 0.7rem; height: 0.7rem"></span>
              <!-- ⚠️ `legendLabel`, which is SHORTER than the hover's version -- "(NOT
                   INVESTABLE)" against "(NOT IMMEDIATELY INVESTABLE)" (AndyP, 2026-09-22). This
                   row is one `whitespace-nowrap` line carrying every run's caption AND figure,
                   and it only appears once the bar is already too cramped, so it lengthens
                   exactly where space is shortest. The hover has no such limit. -->
              <span class="tracking-wide uppercase">{{ seg.legendLabel ?? seg.label }}</span>
              <span class="font-semibold">{{ seg.value | currency }}</span>
            </span>
          }
        </div>
      }
    </div>

    <!-- LEFT TO ALLOCATE -- the empty part of the bar, named.

         The only figure here that ASKS for something: the runs report what the request has
         decided, this one says it is not finished, and it is what blocks Review.

         ⚠️ Caption ABOVE the figure, and no box, unlike every other figure in this strip. Both are
         deliberate: it is the end of the bar rather than an independent number, so it reads as the
         bar's total rather than as a sixth peer, and the caption leading means the eye arrives at
         the label before the amount -- which is how the mockup this came from reads.

         ⚠️ Two tints, not three. Money still to place used to take amber, on the reasoning that an
         outstanding balance wants attention. It reads as a fault instead -- an unplaced balance is
         the NORMAL state of a request being filled in, so amber put a warning colour on screen for
         most of the advisor's time in the wizard. Mint covers both "nothing outstanding" and "left
         to allocate"; red is kept for over-allocation, which genuinely cannot be submitted.

         ⚠️ A THIRD TINT WAS TRIED AND REVERTED. An orange "Short by" state marked the case where
         the cash was spent and only selling would close the gap. The row buttons now say that
         better than a colour can, and "short" named a fault where an outstanding balance is
         simply a request still being filled in (AndyP, 2026-09-24). Two tints again.

         🔴 AND RED LEAVES over-distribution, because that is no longer unsubmittable. A
         distribution may raise more than it pays out; the surplus stays in the account as cash,
         and the check states it as done with the excess named beneath. Red still owns
         over-allocation and over-contribution, where the money genuinely does not exist. -->
    <div class="flex flex-col items-end text-right" style="min-width: 14rem">
      <!-- One caption, naming the state. Each keeps its own title: the caption says WHAT this is,
           the tooltip says what to do about it, and the message beside Next spells out every way
           to resolve it. -->
      <!-- ⚠️ `remainderCaption()` / `remainderTooltip()`, NOT literals. These three states were
           hardcoded with the reallocation's words, so a distribution read "Left to allocate" —
           allocation vocabulary on a request that touches no target allocation — and a
           contribution read it too.

           The grey track's tooltip has used `remainderCaption()` since ACMTECH-989, and its
           comment there claims "the same words the figure beside the bar uses". That was not
           true while these were literals; it is now. -->
      @if (investableTerm().needsPlacing) {
        <span class="text-xs tracking-wide uppercase text-mint-darkest"
          [title]="remainderTooltip()">{{ remainderCaption() }}</span>
        <span class="text-lg font-bold text-mint-darker">{{ investableTerm().value | currency }}</span>
      } @else if (investableTerm().overBudget && store.isDistribution()) {
        <!-- Over on a distribution is DONE, not broken, so it borrows the satisfied state's check
             and adds the one fact the check cannot carry: where the extra money went. Without this
             line the request raises more than it pays out and nothing on screen says so. -->
        <span class="flex flex-col items-center gap-y-1">
          <span class="text-xs tracking-wide uppercase text-mint-darkest">{{ remainderCaption() }}</span>
          <i class="material-icons text-mint-darker"
            style="font-size: 2.4rem; -webkit-text-stroke: 0.08rem currentColor"
            [title]="remainderTooltip()">check</i>
          <span class="text-xs text-gray">{{ -investableTerm().value | currency }} stays as cash</span>
        </span>
      } @else if (investableTerm().overBudget) {
        <span class="text-xs tracking-wide uppercase text-red-dark"
          [title]="remainderTooltip()">{{ remainderCaption() }}</span>
        <!-- Signed, so the figure reads as the negative it is rather than as an amount still to
             place. Red carries which of the two it is; the sign stops them looking alike. -->
        <span class="text-lg font-bold text-red-dark">{{ investableTerm().value | currency }}</span>
      } @else {
        <!-- ⚠️ A CHECK, NOT "$0.00". Zero is the one figure here that says nothing: every other
             state of this cell is an amount the advisor has to act on, so a zero sat in the same
             slot reading like a balance of nothing rather than like "done". The check states the
             outcome instead, and it is the same glyph the step row uses for a step that has been
             through -- so "finished" looks the same in both places.

             Mint, matching the figure it replaces, so the cell does not change colour on the way
             to being satisfied.

             ⚠️ CENTRED under the caption via its own column, not by right-aligning with everything
             else. The cell is `items-end` for the money states, where a right edge lines the
             figures up with the table below; a glyph is much narrower than its label, so inheriting
             that put it under the caption's last letter. This wrapper is right-aligned as a block
             and centres its own two children.

             ⚠️ BOLDER via `-webkit-text-stroke`, because `font-weight` does nothing to a ligature
             icon font -- Material Icons ships one weight and has no variable axis here. The stroke
             thickens the drawn glyph itself, in `currentColor`, so it cannot fall out of step with
             the mint around it. Supported in Chromium, WebKit and Gecko alike. -->
        <span class="flex flex-col items-center gap-y-1">
          <span class="text-xs tracking-wide uppercase text-mint-darkest">{{ remainderCaption() }}</span>
          <i class="material-icons text-mint-darker"
            style="font-size: 2.4rem; -webkit-text-stroke: 0.08rem currentColor"
            [title]="remainderTooltip()">check</i>
        </span>
      }
    </div>
  }
</div>
